/**
 * Analysis feature toggles (D1 feature_toggles, admin-editable) and the globe
 * view settings. Analysis toggles re-run the analysis; view settings only
 * change what the globe draws.
 */
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { loaderApi } from '../../data/api';
import { useLoader } from '../../store';
import type { OrbitPathMode } from '../../globe/GlobeEngine';
import { usePresentation } from '../presentation';

function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-control px-2 py-cell-y hover:bg-overlay',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 h-4 w-7 shrink-0 rounded-full border transition',
          checked ? 'border-accent bg-accent/80' : 'border-hairline-strong bg-inset',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'absolute top-0.5 h-2.5 w-2.5 rounded-full transition-[left]',
            checked ? 'left-3.5 bg-accent-ink' : 'left-0.5 bg-ink-3',
          )}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-ink">{label}</span>
        {description && <span className="block leading-snug text-ink-3">{description}</span>}
      </span>
    </label>
  );
}

export function SettingsPanel() {
  const dataset = useLoader((s) => s.dataset);
  const toggles = useLoader((s) => s.toggles);
  const setToggle = useLoader((s) => s.setToggle);
  const view = useLoader((s) => s);
  const contactStyle = usePresentation((s) => s.contactStyle);
  const setContactStyle = usePresentation((s) => s.setContactStyle);

  const change = async (id: string, enabled: boolean) => {
    const previous = toggles[id] ?? true;
    setToggle(id, enabled);
    if (dataset?.source !== 'api') return;
    try {
      await loaderApi.setFeatureToggle(id, enabled);
    } catch (err: unknown) {
      setToggle(id, previous);
      const message = err instanceof Error ? err.message : String(err);
      toast('error', `Toggle not saved (${message}). Admin rights are needed to change analysis toggles.`);
    }
  };

  const byCategory = new Map<string, NonNullable<typeof dataset>['toggles']>();
  for (const t of dataset?.toggles ?? []) {
    const cat = t.category ?? 'general';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(t);
  }

  return (
    <div className="flex flex-col gap-section p-panel text-data">
      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Globe</div>
        <Switch
          label="3D coverage domes"
          description="Cones in the 3D view instead of flat circles."
          checked={view.coneMode}
          onChange={(v) => view.setView({ coneMode: v })}
        />
        <Switch
          label="Contact lines"
          checked={view.contactLinesEnabled}
          onChange={(v) => view.setView({ contactLinesEnabled: v })}
        />
        <Switch
          label="Curved contact lines"
          description="Off: straight line of sight. On: schematic curves."
          checked={contactStyle === 'schematic'}
          onChange={(v) => setContactStyle(v ? 'schematic' : 'line-of-sight')}
        />
        <Switch
          label="Active coverage rings"
          checked={view.coverageRingsEnabled}
          onChange={(v) => view.setView({ coverageRingsEnabled: v })}
        />
        <div className="flex items-center gap-3 px-2 py-cell-y">
          <span className="text-ink">Orbit paths</span>
          <select
            aria-label="Orbit path mode"
            value={view.orbitPathMode}
            onChange={(e) => view.setView({ orbitPathMode: e.target.value as OrbitPathMode })}
            className="ml-auto rounded-control border border-hairline bg-inset px-2 py-1 text-xs text-ink"
          >
            <option value="selected">Selected mission</option>
            <option value="all">All missions</option>
            <option value="none">None</option>
          </select>
        </div>
        <Switch
          label="World terrain"
          description="Cesium ion terrain with water mask."
          checked={view.terrain}
          onChange={(v) => view.setView({ terrain: v })}
        />
        <Switch
          label="Photorealistic 3D tiles"
          description="Loads below 50 km camera height."
          checked={view.google3D}
          onChange={(v) => view.setView({ google3D: v })}
        />
        <Switch
          label="OSM buildings"
          checked={view.osmBuildings}
          onChange={(v) => view.setView({ osmBuildings: v })}
        />
      </div>

      {[...byCategory.entries()].map(([cat, list]) => (
        <div key={cat}>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">{cat} toggles</div>
          {list.map((t) => (
            <Switch
              key={t.id}
              label={t.name}
              description={t.description}
              checked={toggles[t.id] ?? true}
              onChange={(v) => void change(t.id, v)}
            />
          ))}
        </div>
      ))}
      {dataset?.source === 'static' && (
        <div className="text-ink-3">Offline data: analysis toggles apply locally only.</div>
      )}
    </div>
  );
}
